import { IEmployee, ConsultantOption } from "../../types/employee.type";
import { ApiResponseType, makeRequest } from "../helpers/api.helpers";
import { createDebounce } from "../helpers/debounce.helper";

const API_URL = `${import.meta.env.VITE_API_URL}/employees`;

const makeRequestDebounced = createDebounce(makeRequest, 200);
export const getEmployees = async ({
  search = "",
  page = 1,
  itemsPerPage = "10",
  orderBy = "",
  fields = "companyId, name, type, email, phone",
}: {
  status?: string[];
  search?: string;
  page?: number;
  itemsPerPage?: string;
  orderBy?: string;
  fields?: string;
  companyId?: string;
}) => {
  const params = new URLSearchParams({
    page: String(page),
    itemsPerPage: String(itemsPerPage),
    fields,
  });

  search && params.append("search", search);
  orderBy && params.append("orderBy", orderBy);

  const url = `${API_URL}/?${params.toString()}`;
  const result = (await makeRequestDebounced(url)) as ApiResponseType<
    IEmployee[]
  > & { totalItems: number };
  return { ok: result.ok, data: result.data, totalItems: result.totalItems };
};

export async function getConsultantOptionsFromAPI(
  companyIds?: string[],
  currentUF?: string,
  type?: "Consultor" | "PosVenda",
  nameFilter?: string
): Promise<ConsultantOption[]> {
  const url = new URL(`${API_URL}/consultants`);

  if (companyIds && companyIds.length > 0) {
    url.searchParams.append("companyId", companyIds.join(","));
  }
  if (currentUF) {
    url.searchParams.append("currentUF", currentUF);
  }
  if (type) {
    url.searchParams.append("type", type);
  }
  if (nameFilter) {
    url.searchParams.append("nameFilter", nameFilter);
  }
  try {
    const result = (await makeRequest(url.toString())) as ApiResponseType<
      ConsultantOption[]
    >;

    if (result.ok && Array.isArray(result.data)) {
      return [{ value: "", label: "Selecione..." }, ...result.data];
    }

    return [{ value: "", label: "Selecione..." }];
  } catch (error) {
    console.error("Falha ao buscar opções de consultores:", error);
    return [{ value: "", label: "Erro ao carregar" }];
  }
}

export const getAllEmployees = async (name?: string) => {
  const url = new URL(`${API_URL}/all`);

  if (name) {
    url.searchParams.append("name", name);
  }

  const result = (await makeRequestDebounced(
    url.toString()
  )) as ApiResponseType<IEmployee[]>;

  return { ok: result.ok, data: result.data };
};

export async function removeAccessEmployee(
  employeeId: string,
  description: string
) {
  const url = `${API_URL}/${employeeId}`;
  const options = {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ description }),
  };

  return makeRequest(url, options) as Promise<ApiResponseType<unknown>>;
}

export const getPreparators = async () => {
  const url = `${API_URL}/type/preparator`;
  const result = (await makeRequestDebounced(url)) as ApiResponseType<
    IEmployee[]
  >;
  return { ok: result.ok, data: result.data };
};

export const getMechanics = async () => {
  const url = `${API_URL}/type/mechanics`;
  const result = (await makeRequestDebounced(url)) as ApiResponseType<
    IEmployee[]
  >;
  return { ok: result.ok, data: result.data };
};

export async function getEmployeeById(employeeId: string) {
  const url = `${API_URL}/${employeeId}`;

  const result = (await makeRequestDebounced(url)) as Promise<
    ApiResponseType<IEmployee>
  >;

  return result;
}

export async function getEmployeeByType() {
  const url = `${API_URL}/consultant/type`;
  const result = (await makeRequestDebounced(url)) as Promise<
    ApiResponseType<IEmployee>
  >;

  return result;
}

export async function getEmployeeConsultant() {
  const url = `${API_URL}/consultants`;
  const result = (await makeRequestDebounced(url)) as Promise<
    ApiResponseType<IEmployee>
  >;

  return result;
}

const makeRequestUpdateDebounced = createDebounce(makeRequest, 200);
export async function updateEmployee(id: string, data: IEmployee) {
  const url = `${API_URL}/${id}`;
  const options = {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...data }),
  };

  const result = makeRequestUpdateDebounced(url, options) as Promise<
    ApiResponseType<IEmployee>
  >;

  return result;
}

const makeRequestCreateDebounced = createDebounce(makeRequest, 200);
const removeReadonlyFields = (updatedData) => {
  delete updatedData._id;
  delete updatedData.__v;
};

export async function createEmployee(data: IEmployee) {
  removeReadonlyFields(data);

  const url = `${API_URL}/`;
  const options = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  };
  return makeRequestCreateDebounced(url, options) as Promise<
    ApiResponseType<IEmployee>
  >;
}
